import { useCallback, useMemo } from "react";
import SparketModal from "components/common/modal/modal.tsx";
import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import {
  getDepositFundsModalState,
  hideDepositModalV2,
  setSelectedAmount,
  setProcessing,
} from "state/slices/deposit-funds-modal-v2-slice.ts";
import { showGlobalLoadingOverlay, hideGlobalLoadingOverlay } from "state/slices/global-loading-overlay-slice.ts";
import { fetchRealWallet } from "state/slices/user-real-wallet-slice.ts";
import { fetchOrders } from "state/slices/user-orders-slice.ts";
import { fetchBankAccounts, getBankDepositMinimum } from "state/slices/bank-accounts-slice.ts";
import { postRequest } from "utils/httpClient.ts";
import { reportError } from "utils/report-error";
import { DepositFundsRequest } from "interfaces/commerce/order/create-order-request.ts";
import { Order } from "interfaces/commerce/order/order.ts";
import { ErrorResponseBody } from "interfaces/error-response-body.ts";
import { ErrorCodes } from "interfaces/error-codes.ts";
import { toast } from "react-toastify";
import GreyModalSection from "components/commerce/grey-modal-section.tsx";
import BalanceDisplay from "components/common/real-money-wallet/balance-display.tsx";
import DepositFundsModalPaymentMethodsBlock from "./deposit-funds-modal-payment-method-block.tsx";
import DepositButton from "./deposit-button.tsx";
import AmountSelector from "../amount-selector.tsx";
import DepositNote from "./deposit-note.tsx";
import CryptoPaymentBlock from "./crypto-payment-block.tsx";
import SpeedPaymentBlock from "./speed-payment-block.tsx";
import OrSeparator from "../shared/or-separator.tsx";
import { getOrdersPath } from "utils/backend-path-builders.ts";
import { PaymentStatus } from "components/payment-provider/apple-pay/apple-pay-button.tsx";
import { getAppConfig } from "state/slices/app-config-slice.ts";

import { getGroupState } from "state/slices/group-slice.ts";
import { getIntegratedAppState } from "state/slices/integrated-app-slice.ts";
import useWallet from "hooks/useWallet.tsx";
import { Spinner } from "react-bootstrap";
import BlockyBoldText from "components/custom-texts/blocky-bold-text.tsx";

const DepositFundsModal = () => {
  const dispatch = useAppDispatch();
  const modalState = useAppSelector(getDepositFundsModalState);
  const bankDepositMinimum = useAppSelector(getBankDepositMinimum);
  const appConfig = useAppSelector(getAppConfig);

  const group = useAppSelector(getGroupState);
  const integratedAppState = useAppSelector(getIntegratedAppState);
  const isSpeedIntegration = group.name === "Speed" && integratedAppState.isIntegratedApp;

  const { formattedBalance, loading: walletLoading } = useWallet();

  const handleClose = useCallback(() => {
    dispatch(hideDepositModalV2());
  }, [dispatch]);

  const handleAmountChange = useCallback((amount: number) => {
    dispatch(setSelectedAmount(amount));
  }, [dispatch]);

  const createOrder = useCallback(async (applePayToken?: string, googlePayToken?: string): Promise<Order> => {
    const { selectedAmount, selectedPaymentMethod } = modalState;

    if (!applePayToken && !googlePayToken && (!selectedPaymentMethod || !selectedAmount)) {
      throw new Error('Missing payment method or amount');
    }

    const request: DepositFundsRequest = {
      amount: selectedAmount,
      orderType: "DEPOSIT_FUNDS",
      successPathname: window.location.pathname + window.location.search
    };

    if (applePayToken) {
      request.applePayToken = applePayToken;
      request.paymentMethodType = "APPLE_PAY";
    } else if (googlePayToken) {
      request.googlePayToken = googlePayToken;
      request.paymentMethodType = "GOOGLE_PAY";
    } else if (selectedPaymentMethod?.type === 'CARD') {
      if (selectedPaymentMethod.inovioUserPaymentMethodId !== 'NEW') {
        request.userPaymentMethodId = selectedPaymentMethod.inovioUserPaymentMethodId;
      }
      request.paymentMethodType = "CARD";
    } else if (selectedPaymentMethod?.type === 'ACH') {
      if (selectedPaymentMethod.provider === 'PLAID') {
        request.plaidAccountId = selectedPaymentMethod.bankAccountId;
      } else {
        request.paynoteFundingSourceId = selectedPaymentMethod.bankAccountId;
      }
      request.paymentMethodType = "ACH";
    }

    const response = await postRequest(getOrdersPath(), { body: request });
    return response as Order;
  }, [modalState]);

  const handleDirectPayment = useCallback(async () => {
    dispatch(showGlobalLoadingOverlay({ message: "Processing payment..." }));

    try {
      const order = await createOrder();
      dispatch(fetchOrders());

      if (order.status === 'COMPLETED') {
        dispatch(fetchRealWallet());
        handleClose();
      } else if (order.status === 'PROCESSING') {
        toast.info('Deposit is being processed...');
        dispatch(fetchRealWallet());
        handleClose();
      } else if (order.status === 'PAYMENT_FAILED') {
        toast.error('Payment failed. Please try a different payment method or contact support.');
      } else if (order.status === 'ERROR') {
        toast.error('An error occurred processing your payment. Please contact support.');
      } else {
        toast.warning(`Order status: ${order.status}. Please check your orders or contact support.`);
        handleClose();
      }
    } catch (error: any) {
      const errorResponse: ErrorResponseBody | undefined = error.response?.data;
      if (errorResponse) {
        const error_code = errorResponse.error_code;
        switch (error_code) {
          case ErrorCodes.DepositLimitationExceeded:
          case ErrorCodes.BankDepositDeclined:
          case ErrorCodes.BankProviderNotEnabled:
            toast.error(errorResponse.friendly_error_message);
            break;
          case ErrorCodes.BankReconnectRequired:
            toast.error(errorResponse.friendly_error_message);
            dispatch(fetchBankAccounts());
            break;
          default:
            toast.error("Something went wrong. Please contact support.");
        }
      } else {
        toast.error("Something went wrong. Please contact support.");
      }
      reportError(error, "deposit", { error_code: errorResponse?.error_code });
    } finally {
      dispatch(hideGlobalLoadingOverlay());
    }
  }, [createOrder, dispatch, handleClose]);

  const handleHostedPaymentRedirect = useCallback(async () => {
    dispatch(showGlobalLoadingOverlay({ message: "Preparing payment page..." }));

    try {
      const order = await createOrder();

      if (order.payment_page_url) {
        window.location.href = order.payment_page_url;
      } else {
        throw new Error('No payment page URL received from server');
      }
    } catch (error: any) {
      const errorResponse: ErrorResponseBody | undefined = error.response?.data;
      if (errorResponse) {
        const error_code = errorResponse.error_code;
        switch (error_code) {
          case ErrorCodes.DepositLimitationExceeded:
            toast.error(errorResponse.friendly_error_message);
            break;
          default:
            toast.error("Failed to prepare payment page. Please contact support.");
        }
      } else {
        toast.error("Failed to prepare payment page. Please contact support.");
      }
      dispatch(hideGlobalLoadingOverlay());
    }
  }, [createOrder, dispatch]);

  const handleApplePayPayment = useCallback((applePayToken: string) => {
    dispatch(setProcessing(true));
    return createOrder(applePayToken)
        .catch(error => {
          const errorResponse: ErrorResponseBody | undefined = error.response?.data;
          if (errorResponse) {
            const error_code = errorResponse.error_code;
            switch (error_code) {
              case ErrorCodes.DepositLimitationExceeded:
                toast.error(errorResponse.friendly_error_message);
                return;
            }
          }
          toast.error("Something went wrong. Please contact support.");
          return PaymentStatus.FAILURE;
        })
        .then((order: Order | undefined | PaymentStatus.FAILURE) => {
          if (order === PaymentStatus.FAILURE || !order) {
            return PaymentStatus.FAILURE;
          }
          handleClose();
          return order.status === "COMPLETED" ? PaymentStatus.SUCCESS : PaymentStatus.FAILURE;
        })
        .finally(() => {
          dispatch(fetchRealWallet());
          dispatch(fetchOrders());
          dispatch(setProcessing(false));
        });
  }, [createOrder, dispatch, handleClose]);

  const handleGooglePayPayment = useCallback((googlePayToken: string) => {
    dispatch(setProcessing(true));
    return createOrder(undefined, googlePayToken)
        .catch(error => {
          const errorResponse: ErrorResponseBody | undefined = error.response?.data;
          if (errorResponse) {
            const error_code = errorResponse.error_code;
            switch (error_code) {
              case ErrorCodes.DepositLimitationExceeded:
                toast.error(errorResponse.friendly_error_message);
                return;
            }
          }
          toast.error("Something went wrong. Please contact support.");
          return PaymentStatus.FAILURE;
        })
        .then((order: Order | undefined | PaymentStatus.FAILURE) => {
          if (order === PaymentStatus.FAILURE || !order) {
            return PaymentStatus.FAILURE;
          }
          handleClose();
          return order.status === "COMPLETED" ? PaymentStatus.SUCCESS : PaymentStatus.FAILURE;
        })
        .finally(() => {
          dispatch(fetchRealWallet());
          dispatch(fetchOrders());
          dispatch(setProcessing(false));
        });
  }, [createOrder, dispatch, handleClose]);

  const shouldUseInovioHostedPaymentPage =
      modalState.selectedPaymentMethod?.type === 'CARD' &&
      modalState.selectedPaymentMethod.inovioUserPaymentMethodId === 'NEW';

  const handleDeposit = shouldUseInovioHostedPaymentPage ? handleHostedPaymentRedirect : handleDirectPayment;

  const amountOptions = useMemo(() => {
    const baseAmounts = [5, 20, 100];
    const isACHSelected = modalState.paymentMethodType === 'ACH';

    return baseAmounts.map(amount => ({
      amount,
      disabled: isACHSelected && amount < bankDepositMinimum
    }));
  }, [modalState.paymentMethodType, bankDepositMinimum]);

  return (
      <SparketModal title="Deposit Funds" show={modalState.show} onClose={handleClose}>
        <div style={{ position: "relative" }}>
          {modalState.processing && (
              <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(0, 0, 0, 0.5)",
                    zIndex: 10,
                  }}
              />
          )}

          <GreyModalSection>
            {isSpeedIntegration ? (
                <div className="d-flex flex-column align-items-center py-2">
                  <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px" }}>
                    Current Balance
                  </div>
                  {walletLoading ? (
                      <Spinner animation="border" size="sm" variant="light" className="mt-2" />
                  ) : (
                      <BlockyBoldText style={{ color: "white", fontSize: "24px", marginTop: "4px" }}>
                        {formattedBalance}
                      </BlockyBoldText>
                  )}
                </div>
            ) : (
                <BalanceDisplay />
            )}
          </GreyModalSection>

          {/* Speed Wallet Mini App: show the native Lightning deposit flow and
              nothing else. For non-Speed users, fiat methods are shown first,
              followed by the GLIDE crypto block (when the feature flag is on). */}
          {isSpeedIntegration && <SpeedPaymentBlock onClose={handleClose} />}

          {/* Скрываем фиатный блок, если это Speed */}
          {!isSpeedIntegration && (
              <>
                <GreyModalSection>
                  <AmountSelector
                      presetAmounts={amountOptions}
                      selectedAmount={modalState.selectedAmount}
                      setSelectedAmount={handleAmountChange}
                      placeholder="$ other amount"
                  />
                </GreyModalSection>

                <GreyModalSection>
                  <DepositFundsModalPaymentMethodsBlock
                      onApplePayPayment={handleApplePayPayment}
                      onGooglePayPayment={handleGooglePayPayment}
                  />
                </GreyModalSection>

                <div className="d-flex flex-column align-items-center">
                  <DepositButton onDeposit={handleDeposit} />
                  <DepositNote />
                </div>

                {appConfig.showCryptoPayments && (
                    <>
                      <OrSeparator />
                      <CryptoPaymentBlock onClose={handleClose} />
                    </>
                )}
              </>
          )}
        </div>
      </SparketModal>
  );
};

export default DepositFundsModal;